import React from 'react';
import { TrackingPageConfig, TrackingPageCornerRadius, TrackingPageFont } from './types';

/**
 * Merchant theming is applied as CSS custom properties on a wrapper element
 * rather than Tailwind classes, because the values are arbitrary at runtime and
 * Tailwind can only ship classes it saw at build time.
 */

/**
 * Curated stacks that are either self-hosted (HankRnd, in src/fonts) or
 * guaranteed by the OS. Nothing here triggers a third-party font request.
 *
 * Do not add a family without shipping its files: an unavailable name falls
 * through to the next entry, producing an option that silently renders
 * identically to another one. The merchant picks a key, never a
 * font name, so the shopper page makes no third-party font request and no
 * merchant input reaches a `font-family` declaration.
 */
export const FONT_STACKS: Record<TrackingPageFont, string> = {
  system:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  hanken: '"HankRnd", -apple-system, Helvetica, Arial, sans-serif',
  serif: 'Georgia, Cambria, "Times New Roman", Times, serif',
  mono: '"SFMono-Regular", Menlo, Consolas, "Liberation Mono", monospace',
};

const RADII: Record<TrackingPageCornerRadius, string> = {
  sharp: '0px',
  rounded: '12px',
  pill: '24px',
};

const FALLBACK = {
  primaryColor: '#1565c0',
  backgroundColor: '#f6f7fb',
  surfaceColor: '#ffffff',
  textColor: '#0b1c30',
  fontFamily: 'system' as TrackingPageFont,
  cornerRadius: 'rounded' as TrackingPageCornerRadius,
};

/**
 * Build the inline custom-property block for a themed subtree.
 *
 * Tolerates a missing config so the page can render while the query is still in
 * flight without flashing an unstyled layout.
 */
export function configToCssVars(config?: TrackingPageConfig): React.CSSProperties {
  const theme = config?.theme ?? FALLBACK;

  const font = FONT_STACKS[theme.fontFamily as TrackingPageFont] ?? FONT_STACKS[FALLBACK.fontFamily];
  const radius = RADII[theme.cornerRadius as TrackingPageCornerRadius] ?? RADII[FALLBACK.cornerRadius];

  return {
    '--tp-primary': theme.primaryColor || FALLBACK.primaryColor,
    '--tp-bg': theme.backgroundColor || FALLBACK.backgroundColor,
    '--tp-surface': theme.surfaceColor || FALLBACK.surfaceColor,
    '--tp-text': theme.textColor || FALLBACK.textColor,
    '--tp-font': font,
    '--tp-radius': radius,
  } as React.CSSProperties;
}
